import React, { useState, useEffect } from 'react';
import {
	Card,
	CardBody,
	CardHeader,
} from '../../../../_metronic/_partials/controls';
import {
	Button,
	TextField,
	FormControl,
	FormHelperText,
	InputLabel,
	MenuItem,
	Select,
	MuiThemeProvider,
	createMuiTheme,
} from '@material-ui/core';
import { useHistory, useParams } from 'react-router-dom';
import {
	deleteUser,
	getUserById,
	postUser,
	updateUser,
} from '../../../../api/user';
import { useSkeleton } from '../../../hooks/useSkeleton';
import { ROLES, ROLE_LABELS, useAuthUser } from '../../../hooks/useIsAdmin';
import { alertError, alertSuccess } from '../../../../utils/logger';
import ConfirmDialog from '../../../components/dialogs/ConfirmDialog';

// Tema para el botón de borrar (rojo solo en acciones destructivas)
const theme = createMuiTheme({
	palette: {
		secondary: {
			main: '#F64E60',
		},
	},
});

const ROLE_OPTIONS = [
	{ value: ROLES.ADMIN, label: ROLE_LABELS[ROLES.ADMIN] },
	{ value: ROLES.VIEWER, label: ROLE_LABELS[ROLES.VIEWER] },
];

function getEmptyAdmin() {
	return {
		fullName: '',
		email: '',
		role: ROLES.ADMIN,
		password: '',
		repeatPassword: '',
	};
}

export default function EditAdminsPage() {
	const [admin, setAdmin] = useState(getEmptyAdmin());
	const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
	const adminId = useParams().id;
	const history = useHistory();
	const me = useAuthUser();
	const isMe = !!(adminId && me && String(me._id) === String(adminId));

	const {
		isLoading: isLoadingData,
		disableLoading: disableLoadingData,
		ContentSkeleton,
	} = useSkeleton();

	useEffect(() => {
		if (!adminId) {
			disableLoadingData();
			return;
		}
		getUserById(adminId)
			.then((res) => {
				if (res.status === 200) {
					const user = res.data;
					delete user.password;
					setAdmin({
						...getEmptyAdmin(),
						...user,
						role: ROLE_OPTIONS.some((o) => o.value === user.role)
							? user.role
							: ROLES.ADMIN,
						password: '',
						repeatPassword: '',
					});
					disableLoadingData();
				}
			})
			.catch((error) => {
				alertError({
					error: error,
					customMessage: 'No se ha podido cargar el usuario.',
				});
				history.push('/admins');
			});
	}, [adminId, disableLoadingData, history]);

	function saveAdmin() {
		if (!admin.fullName.trim() || !admin.email.trim()) {
			alertError({
				error: null,
				customMessage: 'El nombre y el email son obligatorios.',
			});
			return;
		}
		if (admin.password !== admin.repeatPassword) {
			alertError({
				error: null,
				customMessage: 'Las contraseñas no coinciden.',
			});
			return;
		}
		if (!adminId && !admin.password) {
			alertError({
				error: null,
				customMessage: 'La contraseña es obligatoria.',
			});
			return;
		}
		const body = {
			fullName: admin.fullName.trim(),
			email: admin.email.trim(),
			role: isMe ? ROLES.ADMIN : admin.role,
		};
		if (admin.password) body.password = admin.password;

		if (!adminId) {
			postUser(body)
				.then((res) => {
					if (res.status === 201) {
						alertSuccess({
							title: 'Guardado',
							customMessage: 'Usuario creado correctamente.',
						});
						history.push('/admins');
					}
				})
				.catch((error) => {
					alertError({
						error: error,
						customMessage: 'No se ha podido crear el usuario.',
					});
				});
		} else {
			updateUser(adminId, body)
				.then((res) => {
					if (res.status === 200) {
						alertSuccess({
							title: 'Guardado',
							customMessage: 'Cambios guardados correctamente.',
						});
						history.push('/admins');
					}
				})
				.catch((error) => {
					alertError({
						error: error,
						customMessage: 'No se han podido guardar los cambios.',
					});
				});
		}
	}

	const handleChange = (element) => (event) => {
		setAdmin({ ...admin, [element]: event.target.value });
	};

	if (isLoadingData) return <ContentSkeleton />;
	else
		return (
			<>
				<Card>
					<CardHeader
						title={adminId ? 'Editar usuario' : 'Nuevo usuario'}
					></CardHeader>
					<CardBody>
						<TextField
							id={`fullName`}
							label='Nombre'
							value={admin.fullName}
							onChange={handleChange('fullName')}
							InputLabelProps={{
								shrink: true,
							}}
							margin='normal'
							variant='outlined'
							required
						/>

						<TextField
							id={`email`}
							label='Email'
							value={admin.email}
							onChange={handleChange('email')}
							InputLabelProps={{
								shrink: true,
							}}
							margin='normal'
							variant='outlined'
							required
						/>

						<FormControl
							variant='outlined'
							margin='normal'
							style={{ minWidth: '280px', display: 'block' }}>
							<InputLabel id='role-label' shrink>
								Rol
							</InputLabel>
							<Select
								labelId='role-label'
								id='role'
								label='Rol'
								value={admin.role}
								onChange={handleChange('role')}
								disabled={isMe}
								style={{ minWidth: '280px' }}>
								{ROLE_OPTIONS.map((o) => (
									<MenuItem key={o.value} value={o.value}>
										{o.label}
									</MenuItem>
								))}
							</Select>
							<FormHelperText>
								{isMe
									? 'No puedes cambiar tu propio rol.'
									: admin.role === ROLES.VIEWER
									? 'Solo lectura: ve indicadores y respuestas y descarga informes. No puede modificar nada.'
									: 'Administrador: gestiona sesiones, controla la dinámica y los usuarios.'}
							</FormHelperText>
						</FormControl>

						<TextField
							id={`password`}
							label='Contraseña'
							value={admin.password}
							onChange={handleChange('password')}
							InputLabelProps={{
								shrink: true,
							}}
							type='password'
							margin='normal'
							variant='outlined'
							helperText={
								adminId ? 'Déjala vacía para no cambiarla.' : undefined
							}
							required={!adminId}
						/>
						<TextField
							id={`repeatPassword`}
							label='Repetir contraseña'
							value={admin.repeatPassword}
							onChange={handleChange('repeatPassword')}
							InputLabelProps={{
								shrink: true,
							}}
							type='password'
							margin='normal'
							variant='outlined'
							required={!adminId}
						/>
					</CardBody>
				</Card>
				<Button
					onClick={() => history.push('/admins')}
					variant='outlined'
					style={{ marginRight: '20px' }}>
					Volver
				</Button>
				<Button
					onClick={() => saveAdmin()}
					variant='outlined'
					color='primary'
					style={{ marginRight: '20px' }}>
					Guardar usuario
				</Button>
				{adminId && !isMe && (
					<>
						<MuiThemeProvider theme={theme}>
							<Button
								onClick={() => setOpenConfirmDialog(true)}
								variant='outlined'
								color='secondary'>
								Eliminar usuario
							</Button>
						</MuiThemeProvider>

						<ConfirmDialog
							title={'¿Seguro que quieres eliminar este usuario?'}
							open={openConfirmDialog}
							setOpen={setOpenConfirmDialog}
							onConfirm={() => {
								deleteUser(adminId)
									.then((res) => {
										if (res.status === 204) {
											alertSuccess({
												title: 'Eliminado',
												customMessage:
													'Usuario eliminado correctamente.',
											});
											history.push('/admins');
										}
									})
									.catch((error) => {
										alertError({
											error: error,
											customMessage:
												'No se ha podido eliminar el usuario.',
										});
									});
							}}
						/>
					</>
				)}
			</>
		);
}
